"use client";

import { createId } from "@paralleldrive/cuid2";
import { useCallback, useRef, useState } from "react";

import { z_ChatAttachmentUploadMimeType } from "@lattice/core-lib/types/chat";
import { sha256HexOfBytes } from "@lattice/shared-frontend/hashing";
import type { T_ChatV8Capabilities } from "@lattice/core-lib/types/chat-v8";
import {
  canSendWithChatV8Attachments,
  CHAT_V8_ATTACHMENT_STAGING_LIMIT,
  getAcceptedChatV8AttachmentMimeTypes,
  getChatV8AttachmentKindForMimeType,
  getChatV8SendAttachments,
  getSupportedChatV8AttachmentKinds,
  reduceChatV8AttachmentStaging,
  type T_ChatV8AttachmentStagingEvent,
  type T_ChatV8ClientTransport,
  type T_ChatV8StagedAttachment,
  type T_ChatV8UploadedAttachment,
} from "@lattice/shared-frontend/chat-v8";

export type T_ChatV8AttachmentsApi = {
  accept: string;
  canSend: boolean;
  clear: () => void;
  enabled: boolean;
  remove: (localId: string) => void;
  retry: (localId: string) => void;
  stageFiles: (files: readonly File[]) => void;
  staged: T_ChatV8StagedAttachment[];
  takeSendAttachments: () => T_ChatV8UploadedAttachment[];
};

/**
 * Web upload orchestration around the shared staging reducer. The reducer
 * owns which attachments exist and whether a send may proceed; this hook
 * owns hashing, the grant round-trip, and the direct PUT. Accepted file
 * kinds come from server capabilities, never a hardcoded list.
 */
export function useChatV8Attachments(args: {
  capabilities: Pick<
    T_ChatV8Capabilities,
    "supportedAttachmentKinds" | "supportsUserImageAttachments"
  > | null;
  transport: Pick<T_ChatV8ClientTransport, "requestAttachmentUpload">;
}): T_ChatV8AttachmentsApi {
  const [staged, setStaged] = useState<T_ChatV8StagedAttachment[]>([]);
  const stagedRef = useRef(staged);
  const filesByLocalIdRef = useRef(new Map<string, File>());
  const kinds = getSupportedChatV8AttachmentKinds(args.capabilities);
  const accept = getAcceptedChatV8AttachmentMimeTypes(kinds).join(",");

  const apply = useCallback((event: T_ChatV8AttachmentStagingEvent) => {
    setStaged((previous) => {
      const next = reduceChatV8AttachmentStaging(previous, event);
      stagedRef.current = next;
      return next;
    });
  }, []);

  const uploadFile = useCallback(
    async (localId: string, file: File) => {
      const mimeType = z_ChatAttachmentUploadMimeType.safeParse(file.type);
      if (!mimeType.success) {
        apply({ localId, type: "UPLOAD_FAILED" });
        return;
      }
      try {
        const body = await file.arrayBuffer();
        const hashHex = await sha256HexOfBytes(new Uint8Array(body));
        const grant = await args.transport.requestAttachmentUpload({
          byteSize: body.byteLength,
          fileName: file.name || undefined,
          hash: `sha256:${hashHex}`,
          mimeType: mimeType.data,
        });
        const response = await fetch(grant.uploadUrl, {
          body,
          headers: grant.headers,
          method: "PUT",
        });
        if (!response.ok) throw new Error("Attachment upload failed.");
        apply({
          localId,
          type: "UPLOAD_SUCCEEDED",
          uploaded: grant.attachment,
        });
      } catch {
        apply({ localId, type: "UPLOAD_FAILED" });
      }
    },
    [apply, args.transport],
  );

  const stageFiles = useCallback(
    (files: readonly File[]) => {
      for (const file of files) {
        if (getChatV8AttachmentKindForMimeType(kinds, file.type) === null) {
          continue;
        }
        if (stagedRef.current.length >= CHAT_V8_ATTACHMENT_STAGING_LIMIT) {
          break;
        }
        const localId = createId();
        filesByLocalIdRef.current.set(localId, file);
        apply({ localId, mimeType: file.type, name: file.name, type: "STAGE" });
        void uploadFile(localId, file);
      }
    },
    [apply, kinds, uploadFile],
  );

  const retry = useCallback(
    (localId: string) => {
      const file = filesByLocalIdRef.current.get(localId);
      if (!file) return;
      apply({ localId, type: "RETRY" });
      void uploadFile(localId, file);
    },
    [apply, uploadFile],
  );

  const remove = useCallback(
    (localId: string) => {
      filesByLocalIdRef.current.delete(localId);
      apply({ localId, type: "REMOVE" });
    },
    [apply],
  );

  const clear = useCallback(() => {
    filesByLocalIdRef.current.clear();
    apply({ type: "CLEAR" });
  }, [apply]);

  const takeSendAttachments = useCallback(() => {
    const uploads = getChatV8SendAttachments(stagedRef.current);
    filesByLocalIdRef.current.clear();
    apply({ type: "CLEAR" });
    return uploads;
  }, [apply]);

  return {
    accept,
    canSend: canSendWithChatV8Attachments(staged),
    clear,
    enabled: kinds.length > 0,
    remove,
    retry,
    stageFiles,
    staged,
    takeSendAttachments,
  };
}
